import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { UserPlus, Check, Clock, UserMinus } from "lucide-react";
import { toast } from "sonner";

/**
 * Friend-action button shown on a Profile page (when viewing someone else).
 * Auto-loads the friendship status and renders the right CTA:
 *   - none      → "Add Friend"
 *   - outgoing  → "Request Sent" (click to cancel)
 *   - incoming  → "Accept" / "Decline"
 *   - friends   → "Friends ✓" (click to unfriend)
 */
export default function FriendButton({ otherUserId }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/friends/status/${otherUserId}`);
      setState(data);
    } catch { setState({ status: "none" }); }
  };
  useEffect(() => { load(); }, [otherUserId]);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!state || state.status === "self") return null;

  if (state.status === "friends") {
    return (
      <button onClick={() => run(async () => {
        if (!window.confirm("Remove this friend?")) return;
        await api.delete(`/friends/${otherUserId}`);
        toast.success("Unfriended");
      })} disabled={busy}
        className="border border-secondary/60 text-secondary hover:bg-secondary/10 px-4 py-2 rounded-sm text-xs uppercase tracking-wider flex items-center gap-2"
        data-testid="friend-btn-friends">
        <Check size={14} strokeWidth={2} /> Friends
        <UserMinus size={12} strokeWidth={1.5} className="opacity-60" />
      </button>
    );
  }
  if (state.status === "pending" && state.direction === "outgoing") {
    return (
      <button onClick={() => run(async () => {
        await api.post(`/friends/cancel/${otherUserId}`);
        toast.success("Request cancelled");
      })} disabled={busy}
        className="border border-secondary/40 text-foreground/80 hover:text-foreground px-4 py-2 rounded-sm text-xs uppercase tracking-wider flex items-center gap-2"
        data-testid="friend-btn-pending-outgoing">
        <Clock size={14} strokeWidth={1.5} /> Request Sent
      </button>
    );
  }
  if (state.status === "pending" && state.direction === "incoming") {
    return (
      <div className="flex gap-2">
        <button onClick={() => run(async () => {
          await api.post(`/friends/${state.requestId}/accept`);
          toast.success("Friend added");
        })} disabled={busy}
          className="bg-primary text-primary-foreground px-4 py-2 rounded-sm text-xs uppercase tracking-wider flex items-center gap-2"
          data-testid="friend-btn-accept">
          <Check size={14} strokeWidth={2} /> Accept
        </button>
        <button onClick={() => run(async () => {
          await api.post(`/friends/${state.requestId}/reject`);
        })} disabled={busy}
          className="border border-destructive/60 text-destructive px-3 py-2 rounded-sm text-xs uppercase tracking-wider"
          data-testid="friend-btn-reject">
          Decline
        </button>
      </div>
    );
  }
  return (
    <button onClick={() => run(async () => {
      await api.post("/friends/request", { toUserId: otherUserId });
      toast.success("Friend request sent");
    })} disabled={busy}
      className="bg-primary text-primary-foreground px-4 py-2 rounded-sm text-xs uppercase tracking-wider flex items-center gap-2"
      data-testid="friend-btn-add">
      <UserPlus size={14} strokeWidth={2} /> Add Friend
    </button>
  );
}
